import { Component, computed, inject, Signal } from '@angular/core';
import { Icon } from '../icon/icon';
import { WebSocketService } from '../../services/websocket-service';
import { DisplayAnimations } from '../../directives/display-animations';
import { CoachingIntervalColorRef } from '../../services/color-service';
import { SportType } from '../../models/sport.model';

@Component({
  selector: 'app-resistance',
  imports: [Icon],
  templateUrl: './resistance.html',
  styleUrl: './resistance.scss',
  host: {
    '[class.notInteractive]': '!displayInteractiveVariant()',
    '[class.isInteractive]': 'displayInteractiveVariant()',
    '[class.isNeutral]': 'isRunning()',
    '[style.--interactive-resistance-color]': 'interactiveEquipmentColor()',
    '[style.--interactive-resistance-opacity]': 'interactiveEquipementResitanceOpacity() + "%"',
  },
  hostDirectives: [DisplayAnimations],
})
export class Resistance {
  private webSocketService: WebSocketService = inject(WebSocketService);

  readonly value: Signal<number> = computed(
    () => this.webSocketService.broadcastData()?.equipment.difficultyRate ?? 0,
  );

  readonly isEquipmentInteractive: Signal<boolean> = computed(
    () => this.webSocketService.contentInformation()?.isEquipmentInteractive ?? false,
  );

  private readonly sport: Signal<SportType> = computed(
    () => this.webSocketService.contentInformation()?.sport || 'cycling',
  );

  readonly isRunning: Signal<boolean> = computed(() => this.sport() === 'running');

  readonly displayInteractiveVariant: Signal<boolean> = computed(() => {
    if (this.isRunning()) return true;
    return this.isEquipmentInteractive();
  });

  readonly interactiveEquipmentColor: Signal<string> = computed(() => {
    const difficultyRate: number = this.value();
    if (difficultyRate === 100) return '#1a1918';
    if (difficultyRate > 100) return CoachingIntervalColorRef.expert;
    return CoachingIntervalColorRef.soft;
  });

  readonly interactiveEquipementResitanceOpacity: Signal<number> = computed(() => {
    const difficultyRate: number = this.value();
    if (difficultyRate === 100) return 25;
    if (difficultyRate >= 200 || difficultyRate <= 20) return 100;
    if (difficultyRate >= 180 || difficultyRate <= 40) return 80;
    if (difficultyRate >= 160 || difficultyRate <= 60) return 60;
    if (difficultyRate >= 140 || difficultyRate <= 40) return 40;
    return 20;
  });

  readonly resistanceLevel: Signal<number> = computed(() => {
    const difficultyRate: number =
      this.webSocketService.broadcastData()?.equipment.difficultyRate ?? 0;
    return Math.floor(difficultyRate / 10);
  });
}
